import { TranslateService } from '@ngx-translate/core';
import {
  Component,
  ElementRef,
  AfterViewInit,
  Input,
  HostListener,
} from '@angular/core';
import { ChartComponent } from '../chart.component';
import { select, selectAll, mouse, event } from 'd3-selection';
import { axisBottom, axisLeft } from 'd3-axis';
import { line } from 'd3-shape';
import { zoom, zoomIdentity } from 'd3-zoom';
import { schemeDark2 } from 'd3-scale-chromatic';
import { scaleLinear, scaleOrdinal, scaleTime } from 'd3-scale';

import { bisector, extent } from 'd3-array';
import { timeFormatDefaultLocale, TimeLocaleDefinition } from 'd3-time-format';

@Component({
  selector: 'app-multi-line-chart',
  template: '',
  styleUrls: ['./multi-line-chart.component.scss'],
})
export class MultiLineChartComponent
  extends ChartComponent
  implements AfterViewInit {
  tooltipDots: any;
  constructor(protected el: ElementRef, private translate: TranslateService) {
    super(el);
    timeFormatDefaultLocale({
      decimal: ',',
      thousands: '.',
      dateTime: '%a %b %e %X %Y',
      date: '%d.%m.%Y',
      time: '%Y:%H',
      periods: ['AM', 'PM'],
      days: [
        'niedziela',
        'poniedziałek',
        'wtorek',
        'środa',
        'czwartek',
        'piątek',
        'sobota',
      ],
      shortDays: ['niedz.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.'],
      months: [
        'stycznia',
        'lutego',
        'marca',
        'kwietnia',
        'maja',
        'czerwca',
        'lipca',
        'sierpnia',
        'września',
        'października',
        'listopada',
        'grudnia',
      ],
      shortMonths: [
        'Sty',
        'Lut',
        'Mar',
        'Kwi',
        'Maj',
        'Cze',
        'Lip',
        'Sie',
        'Wrz',
        'Paź',
        'Lis',
        'Gru',
      ],
    } as TimeLocaleDefinition);
  }
  @Input() allGroup: { key: string, sensorName: string, desc?: string }[];
  @Input() data: {
    name: string;
    sensorName: string,
    values: { time: number; value: number }[];
  }[];
  @Input() useGrid = true;
  @Input() unit = '';
  @Input() chartType = 'line';
  @Input() legendTranslationKey: string;
  @Input() showLegend = false;
  @Input() minThreshold: number;
  @Input() maxThreshold: number;
  @Input() zoomToFirstValue: boolean = false;
  myColor: any;
  xAxis: any;
  yAxis: any;
  chartWithLegend: any;
  lastWidth: any;
  zoom: any;
  xAxisElement: any;
  zoomedXAxis: any;
  linesContainer: any;
  clip: any;
  opacity: { [key: string]: boolean };

  @HostListener('window:mouseup')
  onMouseUp() {
    if (this.el.nativeElement.offsetWidth !== this.lastWidth) {
      this.lastWidth = this.el.nativeElement.offsetWidth;
      setTimeout(this.redrawChart.bind(this), 500);
    }
  }

  ngAfterViewInit() {
    if (this.data) {
      this.opacity = this.data.reduce(function (map, data) {
        map[data.name] = true;
        return map;
      }, {});
      this.drawChart();
    }
  }

  drawChart() {
    this.updateChartDimentions();
    if (this.width && this.height && this.data) {
      this.updateDimensions();
      this.initElements();
      this.prepareColors();
      this.addXYAxis();
      this.addClip();
      this.drawLines();
      this.drawThresholds();
      this.drawLegend();
      this.drawTooltip();
    }
  }
  private addClip() {
    this.clip = this.svg
      .append('defs')
      .append('svg:clipPath')
      .attr('id', 'clip')
      .append('svg:rect')
      .attr('id', 'clip-rect')
      .attr('x', '0')
      .attr('y', '0')
      .attr('width', this.width)
      .attr('height', this.height);
  }

  updateChart() {
    // recover the new scale
    this.zoomedXAxis = event.transform.rescaleX(this.xAxis);
    this.xAxisElement
      .call(
        axisBottom(this.zoomedXAxis)
          .tickPadding(20)
          .tickSizeInner(-this.height)
          .tickSizeOuter(10)
      )
      .selectAll('.tick > line')
      .style('stroke', 'lightgrey')
      .style('stroke-opacity', 0.7);
    this.el.nativeElement.querySelector('#linesContainer').remove();
    this.drawLines();
  }

  updateChartDimentions() {
    this.chartElement = this.el.nativeElement;
    this.width = this.chartElement.offsetWidth;
    this.height = this.chartElement.offsetHeight - 30;
  }

  private drawTooltip() {
    const self = this;
    const Tooltip = this.chartWithLegend
      .insert('div', 'svg')
      .style('opacity', 0)
      .attr('id', 'tooltip')
      .attr('class', 'tooltip')
      .style('font-size', '12px')
      .style('display', 'block')
      .style('position', 'absolute')
      .style('background-color', 'white')
      .style('pointer-events', 'none')
      .style('border', 'solid')
      .style('border-width', '1px')
      .style('border-radius', '5px')
      .style('padding', '5px')
      .style('width', '200px');

    // Three function that change the tooltip when user hover / move / leave a cell
    const mouseover = function () {
      Tooltip.style('opacity', 1);
      self.tooltipDots.style('opacity', (d) => {
        return self.allGroup.includes(d.name) ? 1 : 0;
      });
    };
    const mousemove = function (d) {
      const bisect = bisector(function (date: any) {
        return date.time;
      }).left;
      const idx = bisect(d.values, self.zoomedXAxis.invert(mouse(this)[0]));
      Tooltip.html(self.getTooltipHtml(self, d, idx))
        .style('left', mouse(this)[0] + -100 + self.margin.left + 'px')
        .style('top', 0);
      self.tooltipDots
        .attr('cx', (dat) => self.zoomedXAxis(dat.values[idx].time))
        .attr('cy', (dat) => self.yAxis(dat.values[idx].value));
    };
    const mouseleave = function () {
      Tooltip.style('opacity', 0);
      self.tooltipDots.style('opacity', 0);
    };

    this.zoom = zoom()
      .scaleExtent([1, 100])
      .translateExtent([
        [0, 0],
        [this.width, this.height],
      ])
      .extent([
        [0, 0],
        [this.width, this.height],
      ])
      .on('zoom', this.updateChart.bind(this));

    this.tooltipDots = this.svg
      .append('g')
      .attr('id', 'tooltipDots')
      .selectAll('tooltipDots')
      .data(this.data)
      .enter()
      .append('circle')
      .attr('class', 'tooltipDot')
      .style('opacity', 0)
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', 5)
      .attr('stroke', (d) => self.myColor(d.name))
      .attr('stroke-width', '2')
      .attr('fill', '#000')
      .style('pointer-events', 'none');

    this.svg
      .selectAll('chart-area')
      .data(this.data)
      .enter()
      .append('rect')
      .attr('z-index', -1)
      .attr('width', this.width)
      .attr('height', this.height)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .on('mouseover', mouseover.bind(this))
      .on('mousemove', function (d) {
        mousemove.bind(this)(d);
      })
      .on('touchmove', function (d) {
        mousemove.bind(this)(d);
      })
      .on('mouseleave', mouseleave);

    if (this.zoomToFirstValue) {
      this.svg
        .call(this.zoom)
        .transition()
        .duration(1500)
        .call(
          this.zoom.transform,
          zoomIdentity
            .scale(100)
            .translate(
              -this.xAxis(
                this.data[0].values.find((value) => value.value > 0).time
              ),
              0
            )
        );
    } else {
      this.svg.call(this.zoom);
    }
  }

  private getTooltipHtml(self: this, d: any, idx: number): string {
    let html = '<div>';
    html +=
      '<div>' +
      d.values[idx].time.toLocaleDateString(this.translate.currentLang, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }) +
      '</div>';
    self.allGroup
      .filter((group) => this.opacity[group.key])
      .forEach((group) => {
        console.log('group', group);
        console.log('tooltip', group.sensorName.toString().includes('Sensor') ?
          self.translate.instant(self.legendTranslationKey + group.key) : group.sensorName);
        html += "<div style='color:" + self.myColor(group) + "'>";

        const temperature = self.data.find(
          (dataGroup) => dataGroup.name === group.key
        ).values[idx].value;
        html +=
          '<span>' +
          group.sensorName.toString().includes('Sensor') ?
            self.translate.instant(self.legendTranslationKey + group.key) : group.sensorName +
          ': </span><span ' +
          (self.maxThreshold && temperature > self.maxThreshold
            ? 'style="color: red"'
            : self.minThreshold && temperature < self.minThreshold
            ? 'style="color: #0000cc"'
            : '') +
          '>' +
          temperature +
          self.unit +
          '</span>';
        html += '</div>';
      });
    return (html += '</div>');
  }

  private drawThresholds() {
    if (this.minThreshold) {
      const lowThresholdLine = line()
        .x((d: any) => this.zoomedXAxis(d.time))
        .y(() => this.yAxis(this.minThreshold));

      this.svg
        .selectAll('lowThreshold')
        .data(this.data)
        .enter()
        .append('path')
        .attr('class', 'line')
        .attr('d', (d) => {
          return lowThresholdLine(d.values as any);
        })
        .attr('stroke', '#0000ff')
        .style('stroke-width', 2)
        .style('fill', 'none');
    }
    if (this.maxThreshold) {
      const maxThresholdLine = line()
        .x((d: any) => this.zoomedXAxis(d.time))
        .y(() => this.yAxis(this.maxThreshold));

      this.svg
        .selectAll('maxThreshold')
        .data(this.data)
        .enter()
        .append('path')
        .attr('class', 'line')
        .attr('d', (d) => {
          return maxThresholdLine(d.values as any);
        })
        .attr('stroke', '#ff0000')
        .style('stroke-width', 2)
        .style('fill', 'none');
    }
  }

  private drawLegend() {
    const self = this;
    this.chartWithLegend
      .append('div')
      .style('display', 'flex')
      .style('justify-content', 'center')
      .selectAll('myLegend')
      .data(this.data)
      .enter()
      .append('text')
      .style('cursor', 'pointer')
      .style('padding', '10px')
      .attr('x', function (d, i) {
        return 30 + i * 60;
      })
      .attr('y', 30)
      .text((d) => {
        if (d.desc.toString().includes('Sensor')) {
          return this.translate.instant(this.legendTranslationKey + d.name);
        }
        return d.desc;
      })
      .style('color', (d) => {
        return this.myColor(d.name);
      })
      .style('font-size', 15)
      .on('click', function (d) {
        self.opacity[d.name] = !self.opacity[d.name];

        select(this).style(
          'color',
          self.opacity[d.name] ? self.myColor(d.name) : '#aaaaaa'
        );
        selectAll('.' + d.name)
          .transition()
          .style('opacity', self.opacity[d.name] ? '1' : '0');
      });
  }

  private drawLines() {
    const drawLine = line()
      .defined((d: any) => !isNaN(d.value))
      .x((d: any) => {
        return this.zoomedXAxis(d.time);
      })
      .y((d: any) => {
        return this.yAxis(d.value);
      });

    this.linesContainer = this.svg.append('g').attr('id', 'linesContainer');
    this.linesContainer
      .attr('clip-path', 'url(#clip)')
      .selectAll('myLinesInterpolation')
      .data(
        this.data.map((d) => {
          return {
            name: d.name,
            values: d.values.filter((value: any, idx, arr: any[]) =>
              drawLine.defined()(value, idx, arr)
            ),
          };
        })
      )
      .enter()
      .append('path')
      .attr('class', (d) => {
        return d.name;
      })
      .style('opacity', (d) => {
        return this.opacity[d.name] ? '1' : '0';
      })
      .attr('d', (d) => {
        return drawLine(d.values as any);
      })
      .attr('stroke', '#ccc')
      .style('stroke-width', 2)
      .style('fill', 'none')
      .style('pointer-events', 'none');

    // Add the lines
    this.linesContainer
      .selectAll('myLines')
      .data(this.data)
      .enter()
      .append('path')
      .attr('class', (d) => {
        return d.name;
      })
      .style('opacity', (d) => {
        return this.opacity[d.name] ? '1' : '0';
      })
      .attr('d', (d) => {
        return drawLine(d.values as any);
      })
      .attr('stroke', (d) => this.myColor(d.name))
      .style('stroke-width', 2)
      .style('fill', (d) => this.myColor(d.name))
      .style('fill-opacity', () => {
        return this.chartType === 'area' ? '50%' : '0%';
      })
      .style('pointer-events', 'none');
  }

  private addXYAxis() {
    this.xAxis = scaleTime()
      .domain(extent(this.data[0].values, (d: any) => d.time))
      .range([0, this.width]);
    this.zoomedXAxis = this.xAxis;
    this.yAxis = scaleLinear()
      .domain([
        Math.min(
          0,
          this.minThreshold ? this.minThreshold : 0,
          Math.min(
            ...this.data
              .map((group) =>
                Math.min(
                  ...group.values
                    .map((val) => Number(val.value))
                    .filter((val) => !isNaN(val))
                )
              )
              .filter((val) => !isNaN(val))
          )
        ),
        Math.max(
          0,
          this.maxThreshold ? this.maxThreshold : 0,
          Math.max(
            ...this.data
              .map((group) =>
                Math.max(
                  ...group.values
                    .map((val) => Number(val.value))
                    .filter((val) => !isNaN(val))
                )
              )
              .filter((val) => !isNaN(val))
          )
        ),
      ])
      .nice()
      .range([this.height, 0]);
    this.drawXYAxis();
  }

  private drawXYAxis() {
    this.xAxisElement = this.svg
      .append('g')
      .attr('class', 'grid')
      .attr('transform', 'translate(0,' + this.height + ')');
    this.xAxisElement
      .call(
        axisBottom(this.zoomedXAxis)
          .tickPadding(20)
          .tickSizeInner(-this.height)
          .tickSizeOuter(10)
      )
      .selectAll('.tick > line')
      .style('stroke', 'lightgrey')
      .style('stroke-opacity', 0.7);

    this.svg
      .append('g')
      .attr('class', 'grid')
      .call(
        axisLeft(this.yAxis)
          .tickPadding(15)
          .tickSizeInner(-this.width)
          .tickSizeOuter(10)
      )
      .selectAll('.tick > line')
      .style('stroke', 'lightgrey')
      .style('stroke-opacity', 0.7);
  }

  private prepareColors() {
    this.myColor = scaleOrdinal()
      .domain(this.allGroup.map(group => group.key))
      .range(schemeDark2)
      .bind(this);
  }

  private updateDimensions() {
    this.height = this.height - this.margin.top - this.margin.bottom;
    this.width = this.width - this.margin.left - this.margin.right;
  }

  private initElements() {
    this.chartWithLegend = select(this.chartElement)
      .append('div')
      .attr('id', 'chartWithLegend');
    this.svg = this.chartWithLegend
      .append('svg')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom)
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
  }
}
